"use client"

import type React from "react"
import { useState } from "react"
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import MenuIcon from "@mui/icons-material/Menu"
import RestaurantIcon from "@mui/icons-material/Restaurant"
import CleaningServicesIcon from "@mui/icons-material/CleaningServices"
import Logo from "../logo"

interface NavItem {
  label: string
  href: string
}

const navItems: NavItem[] = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Why Choose Us", href: "#why-choose-us" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
]

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen)
  }

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Logo />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton sx={{ textAlign: "center" }} component="a" href={item.href}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: "center" }}>
            <ListItemText primary="Log in" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              textAlign: "center",
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
              my: 1,
              mx: 2,
              borderRadius: 1,
            }}
          >
            <ListItemText primary="Sign up" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  )

  return (
    <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
            <img
                src={require("../../assets/logo.png")}
                alt="Google Play"
                style={{ width: "50px", height: "50px", backgroundColor: theme.palette.primary.main }}
            />
            <Typography
              variant="h6"
              component="a"
              href="/"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                textDecoration: "none",
                ml: 2
              }}
            >
              Cooks and Maids
            </Typography>
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ ml: "auto" }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
                {navItems.map((item) => (
                  <Button key={item.label} href={item.href} sx={{ mx: 1, color: "text.primary" }}>
                    {item.label}
                  </Button>
                ))}
              </Box>
              <Box>
                <Button variant="outlined" color="primary" sx={{ mr: 1 }}>
                  Log in
                </Button>
                <Button variant="contained" color="primary">
                  Sign up
                </Button>
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  )
}

export default Header
